export type VacationConflict = {
  id: string;
  customer_id: string;
  contract_id: string | null;
  start_date: string;
  end_date: string;
  status: string;
  reason?: string | null;
};

interface SamplePlanningInfo {
  customer_id?: string | null;
  contract_id?: string | null;
  status?: string | null;
  scheduled_date?: string | null;
  appointment_date?: string | null;
}

const PLANNED_STATUSES = new Set(['scheduled', 'appointed']);

const normalizeDate = (value: string | null | undefined) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

const isDateWithinRange = (target: string, start: string, end: string) => {
  const targetDate = normalizeDate(target);
  const startDate = normalizeDate(start);
  const endDate = normalizeDate(end);

  if (!targetDate || !startDate || !endDate) {
    return false;
  }

  return targetDate.getTime() >= startDate.getTime() && targetDate.getTime() <= endDate.getTime();
};

const groupVacationsByCustomer = (vacations: VacationConflict[]) => {
  return vacations.reduce<Record<string, VacationConflict[]>>((acc, vacation) => {
    if (!acc[vacation.customer_id]) {
      acc[vacation.customer_id] = [];
    }
    acc[vacation.customer_id].push(vacation);
    return acc;
  }, {});
};

const getPlannedDates = (sample: SamplePlanningInfo) => {
  const dates: string[] = [];
  if (sample.scheduled_date) {
    dates.push(sample.scheduled_date);
  }
  if (sample.appointment_date) {
    dates.push(sample.appointment_date);
  }
  return dates;
};

export function annotateVacationConflicts<T extends SamplePlanningInfo>(
  samples: T[],
  vacations: VacationConflict[]
): (T & { vacation_conflicts: VacationConflict[] })[] {
  if (!Array.isArray(samples) || samples.length === 0) {
    return [];
  }

  const vacationsByCustomer = groupVacationsByCustomer(vacations);

  return samples.map((sample) => {
    const conflicts: VacationConflict[] = [];
    const customerId = sample.customer_id ?? undefined;
    const status = sample.status?.toLowerCase();

    if (customerId && status && PLANNED_STATUSES.has(status)) {
      const plannedDates = getPlannedDates(sample);
      if (plannedDates.length > 0) {
        const customerVacations = vacationsByCustomer[customerId] || [];
        for (const vacation of customerVacations) {
          if (sample.contract_id && vacation.contract_id && vacation.contract_id !== sample.contract_id) {
            continue;
          }

          const overlaps = plannedDates.some((date) => isDateWithinRange(date, vacation.start_date, vacation.end_date));
          if (overlaps) {
            conflicts.push(vacation);
          }
        }
      }
    }

    return {
      ...sample,
      vacation_conflicts: conflicts,
    };
  });
}

export const formatVacationRange = (vacation: VacationConflict) => {
  const start = normalizeDate(vacation.start_date);
  const end = normalizeDate(vacation.end_date);

  if (start && end) {
    const startLabel = start.toLocaleDateString('de-DE');
    const endLabel = end.toLocaleDateString('de-DE');
    if (start.getTime() === end.getTime()) {
      return startLabel;
    }
    return `${startLabel} – ${endLabel}`;
  }

  if (start) {
    return start.toLocaleDateString('de-DE');
  }

  if (end) {
    return end.toLocaleDateString('de-DE');
  }

  return '';
};

export const getVacationStatusLabel = (status?: string | null) => {
  if (!status) {
    return '';
  }

  switch (status) {
    case 'pending':
      return 'Ausstehend';
    case 'submitted':
      return 'Eingereicht';
    case 'approved':
      return 'Genehmigt';
    case 'planned':
      return 'Geplant';
    case 'rejected':
      return 'Abgelehnt';
    case 'canceled':
      return 'Storniert';
    default:
      return status;
  }
};
